import { TableCell, TableRow } from '@/components/ui/catalyst/table';
import { Checkbox } from '@/components/ui/checkbox';
import { ArtifactType } from '@/services/types/artifact-digest-types';
import { ArtifactUnpacked } from '@/services/types/artifact-types';

import { ArtifactTypeBadge } from './components/ArtifactTypeBadge';
import {
  formatBytes,
  formatDate,
  formatDuration,
  getFileExtension,
} from './utils/artifactFormatters';

interface ArtifactRowProps {
  artifact: ArtifactUnpacked;
  isSelected?: boolean;
  isSelectable?: boolean;
  isChecked?: boolean;
  onSelectionChange?: (id: string, checked: boolean) => void;
}

const getArtifactDisplayData = (artifact: ArtifactUnpacked) => {
  const { artifact_type, created_at, artifact_digest } = artifact;
  const formattedDate = formatDate(created_at);

  switch (artifact_type) {
    case ArtifactType.BLOB: {
      const blob = artifact.blob!;
      const fileSize = formatBytes(blob.file_size);
      const extension = getFileExtension(blob.name);
      const subtitle =
        artifact_digest?.one_line_summary ||
        (extension
          ? `${extension}${fileSize ? ` • ${fileSize}` : ''}`
          : fileSize);

      return {
        title:
          artifact_digest?.title ||
          blob.name ||
          blob.original_filename ||
          'File',
        subtitle,
        date: formattedDate,
      };
    }

    case ArtifactType.TRANSCRIPT: {
      const transcript = artifact.transcript!;
      const call = transcript.conversation;
      const callDetails = call
        ? `${call.is_inbound ? 'Inbound' : 'Outbound'}${formatDuration(call.duration) ? ` • ${formatDuration(call.duration)}` : ''}`
        : undefined;

      return {
        title:
          artifact_digest?.title ||
          call?.name ||
          `${call?.is_inbound ? 'Inbound' : 'Outbound'} Call` ||
          'Transcript',
        subtitle: artifact_digest?.one_line_summary || callDetails,
        date: formattedDate,
      };
    }

    case ArtifactType.CONVERSATION: {
      const conversation = artifact.conversation!;
      const messages = conversation.messages || [];
      const firstMessage = messages[0];
      const messageCount = `${messages.length} message${messages.length !== 1 ? 's' : ''}`;

      return {
        title:
          artifact_digest?.title ||
          firstMessage?.subject ||
          firstMessage?.blurb ||
          (conversation.is_client_sms_conversation
            ? 'SMS Thread'
            : 'Email Thread'),
        subtitle: artifact_digest?.one_line_summary || messageCount,
        date: formattedDate,
      };
    }

    case ArtifactType.STAFF_NOTE: {
      const note = artifact.staff_note!;
      return {
        title: artifact_digest?.title || 'Staff Note',
        subtitle:
          artifact_digest?.one_line_summary ||
          (note.content.length > 60
            ? `${note.content.slice(0, 60)}...`
            : note.content),
        date: formattedDate,
      };
    }

    default: {
      return {
        title: artifact_digest?.title || 'Unknown Artifact',
        subtitle: artifact_digest?.one_line_summary,
        date: formattedDate,
      };
    }
  }
};

export const ArtifactRow = ({
  artifact,
  isSelected = false,
  isSelectable = false,
  isChecked = false,
  onSelectionChange,
}: ArtifactRowProps) => {
  const { id, artifact_type } = artifact;
  const displayData = getArtifactDisplayData(artifact);
  const isProcessing = !artifact.artifact_digest;

  const handleCheckboxChange = () => {
    onSelectionChange?.(id, !isChecked);
  };

  const handleRowClick = () => {
    if (isSelectable) {
      handleCheckboxChange();
    }
  };

  return (
    <TableRow
      href={isSelectable ? undefined : id}
      aria-selected={isSelected}
      className={`group ${isSelectable ? 'cursor-pointer' : ''}`}
      onClick={isSelectable ? handleRowClick : undefined}
    >
      {isSelectable && (
        <TableCell className="w-10 text-center py-3">
          <Checkbox
            checked={isChecked}
            onCheckedChange={() => {}} // Handled by row click
            aria-label={`Select ${displayData.title}`}
            className="pointer-events-none" // Disable direct checkbox interaction
          />
        </TableCell>
      )}

      <TableCell className="w-12 py-3 align-middle">
        <ArtifactTypeBadge type={artifact_type} />
      </TableCell>

      <TableCell className="py-3">
        <div className="max-w-2xl">
          <div className="flex items-center gap-1">
            <h3
              className={`font-medium text-sm leading-tight truncate ${
                isSelected ? 'text-primary' : 'text-foreground'
              }`}
              title={displayData.title}
            >
              {displayData.title}
            </h3>
            {isProcessing && (
              <span
                className="ml-1 h-2 w-2 rounded-full bg-amber-400/80 ring-1 ring-amber-300/40 inline-block align-middle"
                title="Artifact is still processing or may not be complete"
              />
            )}
          </div>
          {displayData.subtitle && (
            <p
              className="text-xs text-muted-foreground/70 leading-tight truncate mt-0.5"
              title={displayData.subtitle}
            >
              {displayData.subtitle}
            </p>
          )}
          {/* Mobile Date */}
          <div className="sm:hidden pt-0.5">
            <span className="text-xs font-mono text-muted-foreground/60">
              {displayData.date}
            </span>
          </div>
        </div>
      </TableCell>

      <TableCell className="w-24 text-right hidden sm:table-cell py-3">
        <span
          className="text-xs font-mono text-muted-foreground/60"
          title={`Created: ${new Date(artifact.created_at).toLocaleString()}`}
        >
          {displayData.date}
        </span>
      </TableCell>
    </TableRow>
  );
};
